import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import './EmailReports.css';

const WEEKDAY_OPTIONS = [
    { value: 1, label: 'Lun' },
    { value: 2, label: 'Mar' },
    { value: 3, label: 'Mié' },
    { value: 4, label: 'Jue' },
    { value: 5, label: 'Vie' },
    { value: 6, label: 'Sáb' },
    { value: 0, label: 'Dom' },
];

const TIMEZONE_OPTIONS = [
    { value: 'America/Bogota', label: 'Colombia (Bogotá)' },
    { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
    { value: 'America/Lima', label: 'Perú (Lima)' },
    { value: 'America/Santiago', label: 'Chile (Santiago)' },
    { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
    { value: 'UTC', label: 'UTC' },
];

const EMPTY_AUDIENCE = {
    emails: '',
    frequency: 'weekly',
    enabled: false,
    scheduleDays: [1, 2, 3, 4, 5],
    scheduleTimes: ['08:00'],
    scheduleTimezone: 'America/Bogota',
};

function normalizeAudience(raw) {
    if (!raw) return { ...EMPTY_AUDIENCE };
    return {
        ...EMPTY_AUDIENCE,
        ...raw,
        scheduleDays: Array.isArray(raw.scheduleDays) ? raw.scheduleDays : EMPTY_AUDIENCE.scheduleDays,
        scheduleTimes: Array.isArray(raw.scheduleTimes) && raw.scheduleTimes.length
            ? raw.scheduleTimes
            : EMPTY_AUDIENCE.scheduleTimes,
        scheduleTimezone: raw.scheduleTimezone || EMPTY_AUDIENCE.scheduleTimezone,
    };
}

function toggleDay(days, day) {
    const set = new Set(days);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    return [...set].sort((a, b) => a - b);
}

function ScheduleFields({ value, onChange }) {
    const times = value.scheduleTimes?.length ? value.scheduleTimes : ['08:00'];

    function setTimes(next) {
        onChange({ ...value, scheduleTimes: next.length ? next : ['08:00'] });
    }

    return (
        <div className="email-reports-schedule">
            <p className="email-reports-schedule-title">Días y horarios de envío</p>
            <p className="text-muted text-sm email-reports-schedule-hint">
                El servidor revisa cada intervalo del cron (por defecto cada hora). Marca los días y las horas en las
                que quieres recibir el resumen.
            </p>

            <fieldset className="email-reports-days">
                <legend className="email-reports-legend">Días de la semana</legend>
                <div className="email-reports-day-grid">
                    {WEEKDAY_OPTIONS.map((d) => (
                        <label key={d.value} className="email-reports-day-chip">
                            <input
                                type="checkbox"
                                checked={(value.scheduleDays || []).includes(d.value)}
                                onChange={() =>
                                    onChange({
                                        ...value,
                                        scheduleDays: toggleDay(value.scheduleDays || [], d.value),
                                    })
                                }
                            />
                            <span>{d.label}</span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <div className="email-reports-times">
                <span className="email-reports-legend">Horarios (24 h)</span>
                <ul className="email-reports-time-list">
                    {times.map((t, idx) => (
                        <li key={`${idx}-${t}`} className="email-reports-time-row">
                            <input
                                type="time"
                                className="input"
                                value={t}
                                onChange={(e) => {
                                    const next = [...times];
                                    next[idx] = e.target.value;
                                    setTimes(next);
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                disabled={times.length <= 1}
                                onClick={() => setTimes(times.filter((_, i) => i !== idx))}
                                aria-label="Quitar horario"
                            >
                                ✕
                            </button>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setTimes([...times, '12:00'])}
                >
                    + Añadir horario
                </button>
            </div>

            <label className="email-reports-label">
                Zona horaria
                <select
                    className="input"
                    value={value.scheduleTimezone || 'America/Bogota'}
                    onChange={(e) => onChange({ ...value, scheduleTimezone: e.target.value })}
                >
                    {TIMEZONE_OPTIONS.map((z) => (
                        <option key={z.value} value={z.value}>
                            {z.label}
                        </option>
                    ))}
                </select>
            </label>
        </div>
    );
}

function AudienceForm({ title, hint, value, onChange, onSendTest, sending }) {
    return (
        <div className="email-reports-block glass-card">
            <h3>{title}</h3>
            {hint ? <p className="text-muted text-sm">{hint}</p> : null}
            <label className="email-reports-label">
                Correos (separados por coma)
                <input
                    type="text"
                    className="input"
                    value={value.emails}
                    onChange={(e) => onChange({ ...value, emails: e.target.value })}
                    placeholder="admin@empresa.com, rrhh@empresa.com"
                />
            </label>
            <label className="email-reports-label">
                Respaldo si no hay días/horarios
                <select
                    className="input"
                    value={value.frequency}
                    onChange={(e) => onChange({ ...value, frequency: e.target.value })}
                >
                    <option value="daily">Diario (legado)</option>
                    <option value="weekly">Semanal (legado)</option>
                </select>
            </label>

            <ScheduleFields value={value} onChange={onChange} />

            <label className="email-reports-check">
                <input
                    type="checkbox"
                    checked={value.enabled}
                    onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
                />
                Activar envío automático
            </label>
            {value.lastSentAt ? (
                <p className="text-xs text-muted">
                    Último envío: {new Date(value.lastSentAt).toLocaleString('es-CO')}
                    {value.lastSentSlot ? ` · franja ${value.lastSentSlot}` : ''}
                </p>
            ) : null}
            <button type="button" className="btn btn-secondary btn-sm" disabled={sending} onClick={onSendTest}>
                Enviar prueba ahora
            </button>
        </div>
    );
}

export default function EmailReports() {
    const [admin, setAdmin] = useState({ ...EMPTY_AUDIENCE });
    const [supervisor, setSupervisor] = useState({ ...EMPTY_AUDIENCE });
    const [smtpConfigured, setSmtpConfigured] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(null);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        api.getEmailReportSettings()
            .then((res) => {
                const d = res?.data || res;
                if (d.admin) setAdmin(normalizeAudience(d.admin));
                if (d.supervisor) setSupervisor(normalizeAudience(d.supervisor));
                setSmtpConfigured(!!d.smtpConfigured);
            })
            .catch(() => setMsg('No se pudo cargar la configuración.'))
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        if (!admin.scheduleDays?.length || !admin.scheduleTimes?.length) {
            setMsg('El reporte de administrador necesita al menos un día y un horario.');
            return;
        }
        if (!supervisor.scheduleDays?.length || !supervisor.scheduleTimes?.length) {
            setMsg('El reporte de supervisores necesita al menos un día y un horario.');
            return;
        }
        setSaving(true);
        setMsg('');
        try {
            const res = await api.saveEmailReportSettings({ admin, supervisor });
            const d = res?.data || res;
            if (d.admin) setAdmin(normalizeAudience(d.admin));
            if (d.supervisor) setSupervisor(normalizeAudience(d.supervisor));
            setMsg('Configuración guardada.');
        } catch (e) {
            setMsg(e.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async (audience) => {
        setSending(audience);
        setMsg('');
        try {
            const res = await api.sendEmailReportNow(audience);
            const r = res?.data || res;
            if (r.skipped === 'no_emails') setMsg('Agrega al menos un correo válido.');
            else if (r.skipped === 'disabled') setMsg('Activa el envío o usa «Enviar prueba» con guardar previo.');
            else setMsg(`Correo de prueba enviado (${r.sent || 0} destinatario(s)).`);
        } catch (e) {
            setMsg(e.message || 'No se pudo enviar (revisa SMTP en el servidor).');
        } finally {
            setSending(null);
        }
    };

    if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

    return (
        <div className="container container-wide animate-in email-reports-page">
            <h2>📧 Reportes por correo</h2>
            <p className="text-muted mb-lg">
                Resumen automático del panel (alertas, señales y quienes no han hecho check-in hoy). Programa los días y
                horarios de envío por audiencia.
            </p>
            {!smtpConfigured ? (
                <div className="glass-card mb-lg" style={{ borderLeft: '4px solid #f59e0b', padding: '1rem' }}>
                    <strong>SMTP pendiente.</strong> En el servidor define <code>SMTP_HOST</code>, <code>SMTP_FROM</code>,{' '}
                    <code>SMTP_USER</code> y <code>SMTP_PASSWORD</code>. Para envíos automáticos:{' '}
                    <code>EMAIL_REPORTS_CRON=1</code> y opcional <code>EMAIL_REPORTS_CRON_MS=60000</code> (cada minuto)
                    para horarios exactos.
                </div>
            ) : null}

            <AudienceForm
                title="Administrador — empresa completa"
                value={admin}
                onChange={setAdmin}
                sending={sending === 'admin'}
                onSendTest={() => sendTest('admin')}
            />

            <AudienceForm
                title="Supervisores — por equipo"
                value={supervisor}
                onChange={setSupervisor}
                sending={sending === 'supervisor'}
                onSendTest={() => sendTest('supervisor')}
            />

            <div className="email-reports-actions">
                <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
                    {saving ? 'Guardando…' : 'Guardar configuración'}
                </button>
            </div>
            {msg ? <p className="email-reports-msg">{msg}</p> : null}
        </div>
    );
}
